import {apsEvents} from "../../shared/dto/aps.events";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import {DefectType} from "../defectType";
import DefectActivity from "./defectActivity";
import ToolActivity from "./toolActivity";
import HourActivity from "./hourActivity";
import JobRunActivity from "./jobRunActivity";
import {LatestTools, MachineActivity, SectionActivity} from "./shopActivity";
import {QualificationHistory} from "./qualificationHistory";
import GobUtil from "../../services/utility/gob.utility";
import * as _ from "lodash";

export interface TopToolChange {
  id: string;
  timeChanged: Date;
  tools: ToolActivity[];
  isToolChange: boolean;
  comments: apsViewModels.ToolChangeDetailsComment[];
}

export interface GobDetailActivity {
  id: string;
  isHour?:boolean;
  isJobRun?:boolean;
}

export interface DowntimeActivity extends apsViewModels.DowntimeActivity {
  isDowntime: boolean;
}

export interface GobHourActivity extends GobDetailActivity {
  isHour: boolean;
  hour: HourActivity;
  activity: (DefectActivity | TopToolChange | DowntimeActivity)[];
  disabled: boolean;
}

export interface GobJobRunActivity extends GobDetailActivity {
  isJobRun:boolean;
  jobRun: JobRunActivity;
  molds:apsViewModels.ToolActivity[];
}

export default class GobActivity {
  id: string;
  name: string;
  gobNumber: number;
  section: SectionActivity;
  latestTools: LatestTools;
  hours: GobHourActivity[];
  molds: ToolActivity[];
  currentMolds: ToolActivity[];
  jobRuns: GobJobRunActivity[];
  hoursAndJobs: GobDetailActivity[];
  qualificationHistory: QualificationHistory;
  isMachineLevel: boolean;
  defects: DefectActivity[];
  private userProfileData: apsViewModels.UserProfileData;
  private tools: apsViewModels.ToolAndType[];
  toolChanges: ToolActivity[];
  private defectTypes: DefectType[];
  private lastMold: ToolActivity;  
  private jobs: apsViewModels.JobItem[];
  private jobProcess: apsViewModels.JobProcess[];  
  private eightyHrTools: string;
  private oneSixtyHrTools: string;

  constructor(
    gobNumber: number,
    maxGobs: number,
    section: SectionActivity,
    tools: apsViewModels.ToolAndType[],
    userProfileData: apsViewModels.UserProfileData,
    defectTypes: DefectType[],
    jobs: apsViewModels.JobItem[],
    jobProcess: apsViewModels.JobProcess[],
    eightyHrTools: string,
    oneSixtyHrTools: string) {

    this.gobNumber = gobNumber;
    this.userProfileData = userProfileData;
    this.isMachineLevel = (!gobNumber);
    this.name = GobUtil.getGobName(gobNumber, maxGobs);
    this.section = section;
    this.tools = tools;
    this.defectTypes = defectTypes;
    this.qualificationHistory = new QualificationHistory();
    this.defects = [];
    this.id = section.machine.id + '.' + section.sectionNumber + '.' + this.gobNumber;
    this.lastMold = undefined;
    this.currentMolds = [];
    this.toolChanges = [];  
    this.jobs = jobs;
    this.jobProcess = jobProcess;
    this.eightyHrTools = eightyHrTools;
    this.oneSixtyHrTools = oneSixtyHrTools;

    this.initializeLatestTools();
    this.initializeHours();
    this.initializeJobRunsAndMolds();
    this.initializeHoursAndJobs();
  }
   
  private initializeLatestTools() {
    this.latestTools = {};
    _.filter(this.tools, {typeName: 'Mold'}).forEach((tool) => {
      this.latestTools[tool.id] = {
        name: tool.name,
        toolChange: undefined
      };
    });
  }

  private createGobHourActivity(hour: HourActivity): GobHourActivity {
    var jobRuns = this.section.machine.shop.jobRuns;
    var jobRun = _.find(jobRuns, jr =>
      jr.startDate.getTime() <= hour.startTime.getTime()
      && (!jr.endDate || jr.endDate.getTime() > hour.startTime.getTime()));            
    return {
      id: hour.id,
      isHour: true,
      hour: hour,
      activity: [],
      disabled: this.getDisabledSections(this.section.machine, jobRun)
        .some(x => this.section.sectionNumber === x)
    };
  }

  private getDisabledSections(machineActivity: MachineActivity, jobRunActivity: JobRunActivity | null): number[] {
    if (!jobRunActivity
      || jobRunActivity.machineDisabledSections === null) {
      return [];
    }    

    let disabledSections = jobRunActivity.machineDisabledSections.filter(x => x.machineId == machineActivity.id);
    if (disabledSections.length === 0) {
      return [];
    }
    return disabledSections[0].disabledSections ? disabledSections[0].disabledSections : [];
  }

  private initializeHours() {
    this.hours = [];
    var hours = this.section.machine.shop.hours;
    _.forEach(hours, (hour: HourActivity) => {
      this.hours.push(this.createGobHourActivity(hour));
    });
  }

  private initializeJobRunsAndMolds() {
    this.molds = [];
    this.jobRuns = [];  
    var jobRuns = this.section.machine.shop.jobRuns;
    _.forEach(jobRuns, (jobRun: JobRunActivity) => {
      this.jobRuns.push({
        id: jobRun.id,
        isJobRun: true,
        jobRun: jobRun,
        molds: []
      });
    });
  }

  private initializeHoursAndJobs() {
    this.hoursAndJobs = [];
    _.forEach(this.hours, (hour: GobHourActivity) => {
      this.hoursAndJobs.push(hour);
      var jobRun = _.find(this.jobRuns, (jr: GobJobRunActivity) =>
        jr.jobRun.endDate && jr.jobRun.endDate.getTime() === hour.hour.startTime.getTime());
      if (jobRun) {
        this.hoursAndJobs.push(jobRun);
      }
    });
  }

  insertHour(index: number, hour: HourActivity) {
    this.hours.splice(index, 0, this.createGobHourActivity(hour));
    var toDelete = this.hours.length - 24;
    if (toDelete > 0) {
      this.hours.splice(23, toDelete);
    }
    this.initializeHoursAndJobs();
  }

  displayHoursAndJobs() {
    return _.take(this.hoursAndJobs, this.section.machine.shop.numberOfColumns);
  }

  moldOnDate(date: Date, excludeId?: string): ToolActivity {
    var sortedMolds = _.sortBy(this.molds, mold => 0 - mold.timeChanged.getTime());
    for (var i = 0; i < sortedMolds.length; i++) {
      var mold = sortedMolds[i];
      if (date.getTime() > mold.timeChanged.getTime() && mold.id !== excludeId) {
        return mold;
      }
    }
    return undefined;
  }

  addDefect(defect: apsViewModels.DefectActivity): void {
    var existing = _.find(this.defects, { id: defect.id });
    if (existing) {
      return;
    }

    var index = this.getHourIndex(defect.timeFound);
    if (index === null) {
      return;
    }

    var hour = this.hours[index];
    var jobRun = hour.hour.jobRun;
    if (jobRun) {
      jobRun.updateHasActivity(this, true);
    }
    var defectActivity = new DefectActivity(defect, this.userProfileData, this.defectTypes);
    hour.activity.push(defectActivity);
    this.defects.push(defectActivity);
  }

  addToolChange(toolChange: apsViewModels.ToolActivity): void {
    var shop = this.section.machine.shop;
    var jobRun: JobRunActivity;
    var hour: GobHourActivity;

    var index = this.getHourIndex(toolChange.timeChanged);
    if (index !== null) {
      hour = this.hours[index];
      jobRun = hour.hour.jobRun;
      if (jobRun) {
        jobRun.addToolChange(this, toolChange);
      }
    } else if (this.isFuture(toolChange.timeChanged)) {
      jobRun = _.last(shop.jobRuns);
      if (jobRun && jobRun.withinJob(toolChange.timeChanged)) {
        jobRun.addToolChange(this, toolChange);
      }
    } else {
      jobRun = _.first(shop.jobRuns);
      if (jobRun && jobRun.withinJob(toolChange.timeChanged)) {
        jobRun.addToolChange(this, toolChange);
      }
    }

    var toolActivity = new ToolActivity(
      toolChange, shop.timeZoneId, jobRun, this.section.machine.number, this.jobs, this.jobProcess, this.eightyHrTools, this.oneSixtyHrTools);         
    if (hour) {
      var topToolChange = _.find(hour.activity, {id: toolChange.toolChangeId}) as TopToolChange;
      if (!topToolChange) {
        topToolChange = {
          id: toolChange.toolChangeId,
          timeChanged: toolChange.timeChanged,
          tools: [],
          isToolChange: true,
          comments: toolChange.rootComments
        };
        hour.activity.push(topToolChange);
      }

      var existing = _.find(topToolChange.tools, { id: toolActivity.id });
      if (!existing) {
        topToolChange.tools.push(toolActivity);
      }
    }

    if (!_.some(this.toolChanges, {id: toolActivity.id})) {
      this.toolChanges.push(toolActivity);
      this.updateLatestTools(toolActivity);
    }

    if (toolActivity.toolIsMold && !_.some(this.molds, {id: toolActivity.id})) {
      this.molds.push(toolActivity);
      this.updateMolds(toolActivity);
    }
  }

  updateToolChange(toolChange: ToolActivity): void {
    var existing = _.find(this.toolChanges, {id: toolChange.id});
    if (!existing) {
      return;
    }

    this.updateLatestTools(toolChange);
    this.updateMolds(toolChange);
  }

  removeToolChange(toolChange: ToolActivity, topToolChange: TopToolChange, gobHour: GobHourActivity): void {
    if (!toolChange) {
      return;
    }

    var existing = _.find(this.toolChanges, {id: toolChange.id});
    if (!existing) {
      return;
    }

    var shop = this.section.machine.shop;
    if (gobHour) {
      let jobRun = gobHour.hour.jobRun;
      if (jobRun) {
        jobRun.removeToolChange(this, toolChange.toolChangeId);
      }
    } else if (this.isFuture(toolChange.timeChanged)) {
      let jobRun = _.last(shop.jobRuns);
      if (jobRun && jobRun.withinJob(toolChange.timeChanged)) {
        jobRun.removeToolChange(this, toolChange.toolChangeId);
      }
    } else {
      let jobRun = _.first(shop.jobRuns);
      if (jobRun && jobRun.withinJob(toolChange.timeChanged)) {
        jobRun.removeToolChange(this, toolChange.toolChangeId);
      }
    }

    _.remove(this.toolChanges, toolChange);
    if (topToolChange) {
      _.remove(topToolChange.tools, toolChange);
      if (topToolChange.tools.length === 0 && gobHour) {
        _.remove(gobHour.activity, topToolChange);
      }
    }
    this.updateLatestTools(toolChange);

    if (toolChange.toolIsMold) {
      _.remove(this.molds, toolChange);
      _.forEach(this.jobRuns, jr => {
        _.remove(jr.molds, toolChange);
      });
      this.currentMolds = _.filter(this.molds, t => !this.isFuture(t.timeChanged));
      this.lastMold = _.last(this.currentMolds);
    }
  }

  addDowntime(downtime: apsViewModels.DowntimeActivity): void {
    var index = this.getHourIndex(downtime.downtimeStart);
    if (index === null) {
      return;
    }

    var downtimeActivity = <DowntimeActivity>_.assign({isDowntime: true}, downtime);
    var hour = this.hours[index];
    var jobRun = hour.hour.jobRun;
    if (jobRun) {
      jobRun.updateHasActivity(this, true);
    }

    var existing = _.find(hour.activity, { id: downtimeActivity.id });
    if (!existing) {
      hour.activity.push(downtimeActivity);
    }
  }

  addQualification(qualification: apsViewModels.QualificationActivity): void {
    this.qualificationHistory.addQualification(qualification);
  }

  deleteQualification(qualification: apsViewModels.QualificationActivity): void {
    this.qualificationHistory.deleteQualification(qualification);
  }

  addDowntimeDuration(downtime: apsEvents.DowntimeDurationAdded): void{
    var index = this.getHourIndex(downtime.downtimeStart);
    if (index === null) {
      return;
    }

    var downtimeFound: DowntimeActivity = <DowntimeActivity>_.find(this.hours[index].activity, {id: downtime.id});

    if (downtimeFound !== undefined) {
      downtimeFound.duration = downtime.duration;
    }
  }

  private updateLatestTools(toolChange: ToolActivity) {
    var toolChanges = _.sortBy(_.filter(this.toolChanges, {toolId: toolChange.toolId}), t => t.timeChanged.getTime());
    var index = _.indexOf(toolChanges, toolChange);
    if (index > 0) {
      toolChanges[index - 1].timeRemoved = toolChange.timeChanged;
    }
    if (index >= 0 && index < (toolChanges.length - 1)) {
      toolChange.timeRemoved = toolChanges[index + 1].timeChanged;
    }

    index = toolChanges.length - 1;
    if (index >= 0 && index < toolChanges.length) {
      toolChanges[index].timeRemoved = null;
    }

    var latestTool = this.latestTools[toolChange.toolId];
    if (!latestTool) {
      latestTool = this.section.latestTools[toolChange.toolId];
      if (!latestTool) {
        latestTool = this.section.machine.latestTools[toolChange.toolId];
      }
    }
    if (latestTool.toolChange !== toolChange
      && (!latestTool.toolChange || latestTool.toolChange.id !== toolChange.id)) {
      latestTool.toolChange = toolChange;
    }
  }

  private updateMolds(mold: ToolActivity) {
    if (!mold.toolIsMold) {
      return;
    }

    this.molds = _.sortBy(this.molds, t => t.timeChanged.getTime());

    var time = mold.timeChanged.getTime();
    _.forEach(this.jobRuns, (jr: GobJobRunActivity) => {
      if (jr.jobRun.startDate.getTime() > time) {
        _.remove(jr.molds, mold);
      } else if ((!jr.jobRun.endDate || jr.jobRun.endDate.getTime() > time)
          && !_.some(jr.molds, {id: mold.id})) {
        jr.molds.push(mold);
      }
      jr.molds = _.sortBy(jr.molds, t => t.timeChanged.getTime());
    });

    this.currentMolds = _.filter(this.molds, t => !this.isFuture(t.timeChanged));
    this.lastMold = _.last(this.currentMolds);
  }

  private getHourIndex(date: Date) {
    for (var i = 0; i < this.hours.length; i++) {
      if (this.hours[i].hour.withinHour(date)) {
        return i;
      }
    }
    return null;
  }

  private isFuture(date: Date) {
    return date.getTime() >= this.hours[0].hour.endTime.getTime();
  }

  addDowntimeComment(downtimeCommentAdded: apsEvents.DowntimeCommentAdded): void{
    var index = this.getHourIndex(downtimeCommentAdded.downtimeStart);
    if (index === null) {
      return;
    }

    var downtimeFound = _.find(this.hours[index].activity, {id: downtimeCommentAdded.downtimeId}) as DowntimeActivity;
    if (!downtimeFound) {
      return;
    }

    var commentFound = _.find(downtimeFound.comments, {id: downtimeCommentAdded.id});
    if (!commentFound) {

      var comments = [];
      if (!_.isEmpty(downtimeFound.comments)) {
        comments = downtimeFound.comments;
      }
      var comment = {
        id: downtimeCommentAdded.id,
        downtimeId: downtimeCommentAdded.downtimeId,
        comment: downtimeCommentAdded.comment,
        createdOn: downtimeCommentAdded.eventDate,
        createdByFullName: downtimeCommentAdded.createdByFullName
      };
      comments.push(comment);
      downtimeFound.comments = comments;
    }
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--5!./src/app/models/activity/gobActivity.ts